import { ReactNode, useEffect, useRef } from 'react';
import { useFetcher, useLocation } from 'react-router-dom';
import { Box, Dialog, styled } from '@mui/material';
import { LdtOrderRepository } from '@ldt/api/order_repository';
import { BillingHoldSection } from '@ldt/features/orders/components/billing_hold_section';
import { OrderActions } from '@ldt/features/orders/components/order_actions/order_actions';
import { OrderInformationSection } from '@ldt/features/orders/components/order_information_section';
import { OrderingProviderSection } from '@ldt/features/orders/components/ordering_provider_section';
import { PathologyLabSection } from '@ldt/features/orders/components/pathology_lab_section';
import { LdtOrderDetailHeaderExtra } from '@ldt/features/orders/order_detail_header_extra';
import {
  createOrderDetailConfiguration,
  OrderDetailContentProviders,
  OrderDetailDisplayer,
} from 'src/distribution_platforms/features/order_details';
import {
  LdtOrderDetailFieldsFragment,
  PathologyLabFieldsFragment,
  OrderStatus,
  PathologyLabType,
  SpecimenType,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { OrderAction } from 'src/types/loaders';
import { notifyError, notifySuccess } from 'src/utils';

export const MIN_DISCHARGE_DATE = new Date(1900, 0, 1); // January 1, 1900

// Responsive form row that stacks on mobile and displays in a row on larger screens
export const ResponsiveFormRow = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 1.5, sm: '30px' },
    }}
  >
    {children}
  </Box>
);

// Fullscreen dialog for LDT order form
export const LdtOrderFormDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    height: '100%',
    width: '100%',
    borderRadius: 0,
    margin: 0,
    padding: '4px',
    [theme.breakpoints.up('sm')]: {
      padding: '24px',
    },
    backgroundColor: theme.palette.grey[100],
  },
}));

export interface OrderField {
  label: string;
  value: string | number | null;
}

/**
 * LDT order detail displayer using centralized configuration
 * Includes uploaded files in the information section (LDT-specific)
 */
export class LdtProstateOrderDetailDisplayer implements OrderDetailDisplayer {
  renderHeaderExtra: OrderDetailDisplayer['renderHeaderExtra'];

  constructor(public orderRepository: LdtOrderRepository) {
    const contentProviders: OrderDetailContentProviders = {
      renderInformation: (order, context) => {
        const workflowType = context.workflowType;
        const isEditingQCPendingOrder =
          order.status === OrderStatus.QualityControlPending;

        return (
          <OrderInformationSection
            order={order as LdtOrderDetailFieldsFragment}
            orderRepository={this.orderRepository}
            userRoles={context.userRoles}
            workflowType={workflowType}
            isEditingQCPendingOrder={isEditingQCPendingOrder}
          />
        );
      },
      renderBillingHold: (order) => (
        <BillingHoldSection
          order={order as LdtOrderDetailFieldsFragment}
          platform={DistributionPlatform.Ldt}
        />
      ),
      renderActions: (order) => (
        <OrderActions
          order={order as LdtOrderDetailFieldsFragment}
          orderRepository={this.orderRepository}
        />
      ),
      // LDT-specific sections
      renderOrderingProvider: (order) => (
        <OrderingProviderSection
          order={order as LdtOrderDetailFieldsFragment}
        />
      ),
      renderPathologyLab: (order) => (
        <PathologyLabSectionWrapper
          order={order as LdtOrderDetailFieldsFragment}
          orderRepository={this.orderRepository}
        />
      ),
    };

    this.sections = createOrderDetailConfiguration(
      DistributionPlatform.Ldt,
      contentProviders,
    );
    this.renderHeaderExtra = (order) => (
      <LdtOrderDetailHeaderExtra
        order={order as LdtOrderDetailFieldsFragment}
      />
    );
  }

  sections: ReturnType<typeof createOrderDetailConfiguration>;
}

/**
 * Wrapper component for Pathology Lab Section that handles updates
 */
const PathologyLabSectionWrapper: React.FC<{
  order: LdtOrderDetailFieldsFragment;
  orderRepository: LdtOrderRepository;
}> = ({ order, orderRepository }) => {
  const fetcher = useFetcher();
  const location = useLocation();
  // Track the last processed result data reference to avoid reprocessing
  const lastProcessedDataRef = useRef<unknown>(null);
  // Track if we've submitted a form (to know we're expecting a result)
  const hasSubmittedRef = useRef<boolean>(false);

  // Track when we submit to know we're expecting a result
  useEffect(() => {
    if (fetcher.state === 'submitting') {
      hasSubmittedRef.current = true;
    }
  }, [fetcher.state]);

  // Handle action results
  useEffect(() => {
    if (
      fetcher.data &&
      fetcher.state === 'idle' &&
      hasSubmittedRef.current &&
      lastProcessedDataRef.current !== fetcher.data
    ) {
      const result = fetcher.data as {
        success: boolean;
        order?: LdtOrderDetailFieldsFragment;
        action: OrderAction;
        error?: string;
      };

      if (result.action === OrderAction.UPDATE_PATHOLOGY_LAB) {
        // Mark this result as processed
        lastProcessedDataRef.current = fetcher.data;
        hasSubmittedRef.current = false;

        if (result.success) {
          notifySuccess('Pathology lab updated successfully.');
        } else {
          notifyError(result.error ?? 'Failed to update pathology lab.');
        }
      }
    }
  }, [fetcher.data, fetcher.state]);

  const handlePathologyLabChange = (
    pathologyLab: PathologyLabFieldsFragment | null,
  ) => {
    if (!pathologyLab) {
      return;
    }

    if (!order.id) {
      notifyError('Order ID is missing.');
      return;
    }

    if (
      pathologyLab.type === PathologyLabType.DirectUpload &&
      order.specimenType === SpecimenType.ProstatePostRp
    ) {
      notifyError(
        'Post-RP orders cannot be assigned to Direct Upload Labs. Please select a supported laboratory.',
      );
      return;
    }

    const formData = new FormData();
    formData.append('action', OrderAction.UPDATE_PATHOLOGY_LAB);
    formData.append('pathologyLabSalesforceId', pathologyLab.id);

    fetcher.submit(formData, {
      method: 'post',
      action: location.pathname,
    });
  };

  return (
    <PathologyLabSection
      order={order}
      orderRepository={orderRepository}
      onPathologyLabChange={handlePathologyLabChange}
    />
  );
};
